import { format, getDay, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { TIMEZONE } from "../../../utils/constants";
import { StaffRemuneration, StaffWeeklyHours, WorkLog } from "../types";
import { applyMonthlyFigureBounds, calculateHours } from "./calculations";
import { convertToHours } from "./general";
import { getWeeklyHoursContractType } from "./monthContractType";

export const STAFF_REMUNERATION_FIELDS = "id,start_date,weekdays,value_per_hour,value_per_client,operations_staff_agreement_id";

const WEEKDAY_NUMBER_BY_KEY: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

const WEEKDAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const WEEKDAY_LABELS: Record<number, string> = { 0: "Dom", 1: "Seg", 2: "Ter", 3: "Qua", 4: "Qui", 5: "Sex", 6: "Sáb" };

// hourly: every scheduled hour, clients included (only Valor hora, or both rates equal). perClient: client hours only.
// hourAndClient: two different rates, the original formula. none: no rate.
export type PayBasis = "hourly" | "perClient" | "hourAndClient" | "none";

export type ServiceProviderPayLine = {
    remunerationId: number | null;
    startDate: string | null;
    weekdays: Array<number>;
    valuePerHour: number;
    valuePerClient: number;
    basis: PayBasis;
    scheduleHours: number;
    nonClientHours: number;
    clientHours: number;
    value: number;
    isUncovered: boolean;
};

type DayHours = { weekday: number; scheduleHours: number; clientHours: number };

type CalculateServiceProviderPayInput = {
    workLogs: Array<WorkLog>;
    remunerations: Array<StaffRemuneration>;
    monthDate: Date;
    weeklyHoursSchedule?: Array<StaffWeeklyHours>;
};

// Directus returns the csv field as an array, but rows saved before the cast hold a comma string, '' or null.
const parseRemunerationWeekdays = (value: StaffRemuneration["weekdays"]) => {
    const tokens = Array.isArray(value) ? value : String(value ?? "").split(",");
    const weekdayNumbers = tokens.map((token) => WEEKDAY_NUMBER_BY_KEY[String(token).trim().toLowerCase()]);
    const weekdaysInDisplayOrder = WEEKDAY_DISPLAY_ORDER.filter((weekdayNumber) => weekdayNumbers.includes(weekdayNumber));
    return weekdaysInDisplayOrder;
};

const coversWeekday = (remuneration: StaffRemuneration, weekday: number) => {
    const weekdays = parseRemunerationWeekdays(remuneration.weekdays);
    const covers = weekdays.length === 0 || weekdays.includes(weekday);
    return covers;
};

const toRemunerationDateKey = (startDate: string | null) => {
    const dateKey = startDate ? startDate.slice(0, 10) : "";
    return dateKey;
};

const parseRate = (value: StaffRemuneration["value_per_hour"]) => {
    const rate = Number(value ?? 0);
    const finiteRate = Number.isFinite(rate) ? rate : 0;
    return finiteRate;
};

const getLisbonDay = (isoDate: string) => {
    const lisbonDate = utcToZonedTime(parseISO(isoDate), TIMEZONE);
    const lisbonDay = { dayKey: format(lisbonDate, "yyyy-MM-dd"), weekday: getDay(lisbonDate) };
    return lisbonDay;
};

const groupHoursByLisbonDay = (workLogs: Array<WorkLog>, monthDate: Date) => {
    const hoursByDay = new Map<string, DayHours>();
    const monthWorkLogs: Array<WorkLog> = applyMonthlyFigureBounds(workLogs, { capToMonth: monthDate });
    monthWorkLogs
        .filter((workLog) => workLog.type === "schedules" || workLog.type === "clients")
        .forEach((workLog) => {
            const { dayKey, weekday } = getLisbonDay(workLog.start_date);
            const dayHours = hoursByDay.get(dayKey) ?? { weekday, scheduleHours: 0, clientHours: 0 };
            const entryHours = convertToHours(calculateHours(workLog.start_date, workLog.end_date));
            if (workLog.type === "schedules") dayHours.scheduleHours += entryHours;
            if (workLog.type === "clients") dayHours.clientHours += entryHours;
            hoursByDay.set(dayKey, dayHours);
        });
    return hoursByDay;
};

const findRemunerationTableForDay = (remunerations: Array<StaffRemuneration>, dayKey: string) => {
    const startedRemunerations = remunerations.filter((remuneration) => toRemunerationDateKey(remuneration.start_date) <= dayKey);
    const latestDateKey = startedRemunerations.reduce((latest, remuneration) => {
        const dateKey = toRemunerationDateKey(remuneration.start_date);
        const newerDateKey = dateKey > latest ? dateKey : latest;
        return newerDateKey;
    }, "");
    const table = startedRemunerations.filter((remuneration) => toRemunerationDateKey(remuneration.start_date) === latestDateKey);
    return table;
};

const resolveRemunerationForDay = (remunerations: Array<StaffRemuneration>, dayKey: string, weekday: number) => {
    const table = findRemunerationTableForDay(remunerations, dayKey);
    const coveringRemunerations = table.filter((remuneration) => coversWeekday(remuneration, weekday));
    const resolution = {
        remuneration: coveringRemunerations.length === 1 ? coveringRemunerations[0] : null,
        isConflict: coveringRemunerations.length > 1,
    };
    return resolution;
};

const getPayBasis = (valuePerHour: number, valuePerClient: number) => {
    const isHourly = valuePerHour > 0 && (valuePerClient === 0 || valuePerClient === valuePerHour);
    const isPerClient = valuePerHour === 0 && valuePerClient > 0;
    const isHourAndClient = valuePerHour > 0 && valuePerClient > 0 && !isHourly;
    const basis: PayBasis = isHourly ? "hourly" : isPerClient ? "perClient" : isHourAndClient ? "hourAndClient" : "none";
    return basis;
};

const createPayLine = (remuneration: StaffRemuneration | null) => {
    const valuePerHour = remuneration ? parseRate(remuneration.value_per_hour) : 0;
    const valuePerClient = remuneration ? parseRate(remuneration.value_per_client) : 0;
    const line: ServiceProviderPayLine = {
        remunerationId: remuneration ? remuneration.id : null,
        startDate: remuneration?.start_date ? toRemunerationDateKey(remuneration.start_date) : null,
        weekdays: remuneration ? parseRemunerationWeekdays(remuneration.weekdays) : [],
        valuePerHour,
        valuePerClient,
        basis: getPayBasis(valuePerHour, valuePerClient),
        scheduleHours: 0,
        nonClientHours: 0,
        clientHours: 0,
        value: 0,
        isUncovered: remuneration === null,
    };
    return line;
};

const calculateDayValue = (line: ServiceProviderPayLine, dayHours: DayHours) => {
    const nonClientHours = dayHours.scheduleHours - dayHours.clientHours;
    const valueByBasis: Record<PayBasis, number> = {
        hourly: dayHours.scheduleHours * line.valuePerHour,
        perClient: dayHours.clientHours * line.valuePerClient,
        hourAndClient: nonClientHours * line.valuePerHour + dayHours.clientHours * line.valuePerClient,
        none: 0,
    };
    const dayValue = valueByBasis[line.basis];
    return dayValue;
};

const addDayToPayLine = (line: ServiceProviderPayLine, dayHours: DayHours) => {
    line.scheduleHours += dayHours.scheduleHours;
    line.nonClientHours += dayHours.scheduleHours - dayHours.clientHours;
    line.clientHours += dayHours.clientHours;
    line.value += calculateDayValue(line, dayHours);
};

const findWeeklyHoursRowForDay = (weeklyHoursSchedule: Array<StaffWeeklyHours>, dayKey: string) => {
    const coveringRows = weeklyHoursSchedule.filter((scheduleRow) => {
        const rowStartKey = scheduleRow.date_start.slice(0, 10);
        const rowEndKey = scheduleRow.date_end ? scheduleRow.date_end.slice(0, 10) : null;
        const coversDay = rowStartKey <= dayKey && (rowEndKey === null || rowEndKey >= dayKey);
        return coversDay;
    });
    const coveringRow = coveringRows.length > 0 ? coveringRows[coveringRows.length - 1] : null;
    return coveringRow;
};

const getDayContractType = (weeklyHoursSchedule: Array<StaffWeeklyHours>, dayKey: string) => {
    const coveringRow = findWeeklyHoursRowForDay(weeklyHoursSchedule, dayKey);
    const dayContractType = getWeeklyHoursContractType(coveringRow?.agreement_type);
    return dayContractType;
};

const getFirstWeekdayPosition = (line: ServiceProviderPayLine) => {
    const position = line.weekdays.length === 0 ? -1 : WEEKDAY_DISPLAY_ORDER.indexOf(line.weekdays[0]);
    return position;
};

const comparePayLines = (lineA: ServiceProviderPayLine, lineB: ServiceProviderPayLine) => {
    const uncoveredOrder = Number(lineA.isUncovered) - Number(lineB.isUncovered);
    const startDateOrder = (lineA.startDate ?? "").localeCompare(lineB.startDate ?? "");
    const weekdayOrder = getFirstWeekdayPosition(lineA) - getFirstWeekdayPosition(lineB);
    const order = uncoveredOrder || startDateOrder || weekdayOrder;
    return order;
};

// A day is priced by the remuneration table in force that Lisbon day (the rows with the latest Data on or
// before it); inside that table, by the one row whose weekdays include the day. No row → 0 €, shown apart.
// Every hour of the month counts, whatever contract run is in force; days under a contract row of Horas Semanais
// are paid by salary and cash days are paid in cash: neither is priced.
export const calculateServiceProviderPay = ({ workLogs, remunerations, monthDate, weeklyHoursSchedule = [] }: CalculateServiceProviderPayInput) => {
    const hoursByDay = groupHoursByLisbonDay(workLogs, monthDate);
    const linesByRemuneration = new Map<number | null, ServiceProviderPayLine>();
    const conflictDayKeys: Array<string> = [];
    let contractScheduleHours = 0;

    hoursByDay.forEach((dayHours, dayKey) => {
        const dayContractType = getDayContractType(weeklyHoursSchedule, dayKey);
        if (dayContractType === "contract") {
            contractScheduleHours += dayHours.scheduleHours;
            return;
        }
        if (dayContractType === "cash") return;
        const { remuneration, isConflict } = resolveRemunerationForDay(remunerations, dayKey, dayHours.weekday);
        if (isConflict) {
            conflictDayKeys.push(dayKey);
            return;
        }
        const lineKey = remuneration ? remuneration.id : null;
        const line = linesByRemuneration.get(lineKey) ?? createPayLine(remuneration);
        addDayToPayLine(line, dayHours);
        linesByRemuneration.set(lineKey, line);
    });

    const lines = Array.from(linesByRemuneration.values()).sort(comparePayLines);
    const totalValue = lines.reduce((sum, line) => sum + line.value, 0);
    const pay = {
        totalValue,
        lines,
        hasConflict: conflictDayKeys.length > 0,
        conflictDayKeys: conflictDayKeys.sort(),
        contractScheduleHours,
    };
    return pay;
};

export type ServiceProviderPay = ReturnType<typeof calculateServiceProviderPay>;

export const groupRemunerationsByAgreement = (remunerations: Array<StaffRemuneration>) => {
    const remunerationsByAgreement = new Map<string, Array<StaffRemuneration>>();
    remunerations.forEach((remuneration) => {
        const agreementKey = String(remuneration.operations_staff_agreement_id);
        const agreementRemunerations = remunerationsByAgreement.get(agreementKey) ?? [];
        agreementRemunerations.push(remuneration);
        remunerationsByAgreement.set(agreementKey, agreementRemunerations);
    });
    return remunerationsByAgreement;
};

export const getAgreementRemunerations = (
    remunerationsByAgreement: Map<string, Array<StaffRemuneration>>,
    agreementId: number | string | undefined,
) => {
    const agreementRemunerations = remunerationsByAgreement.get(String(agreementId)) ?? [];
    return agreementRemunerations;
};

const toCents = (value: number) => {
    const cents = Math.round(value * 100);
    return cents;
};

export const isReceiptDifferentFromPay = (receiptGross: number, calculatedValue: number) => {
    const isDifferent = toCents(receiptGross) !== toCents(calculatedValue);
    return isDifferent;
};

export const getVisiblePayLines = (lines: Array<ServiceProviderPayLine>) => {
    const visibleLines = lines.filter((line) => line.isUncovered || toCents(line.value) !== 0);
    return visibleLines;
};

const groupConsecutivePositions = (positions: Array<number>) => {
    const runs: Array<Array<number>> = [];
    positions.forEach((position) => {
        const lastRun = runs[runs.length - 1];
        if (lastRun && position === lastRun[lastRun.length - 1] + 1) {
            lastRun.push(position);
        } else {
            runs.push([position]);
        }
    });
    return runs;
};

const getLabelAtPosition = (position: number) => {
    const label = WEEKDAY_LABELS[WEEKDAY_DISPLAY_ORDER[position]];
    return label;
};

export const formatRemunerationWeekdays = (weekdays: Array<number>) => {
    if (weekdays.length === 0) {
        const everyDayLabel = "Todos os dias";
        return everyDayLabel;
    }
    const positions = weekdays.map((weekday) => WEEKDAY_DISPLAY_ORDER.indexOf(weekday)).sort((positionA, positionB) => positionA - positionB);
    const runLabels = groupConsecutivePositions(positions).map((run) => {
        const runLabel = run.length >= 3
            ? `${getLabelAtPosition(run[0])}–${getLabelAtPosition(run[run.length - 1])}`
            : run.map(getLabelAtPosition).join(", ");
        return runLabel;
    });
    const label = runLabels.join(", ");
    return label;
};
